import { useEffect, useState } from "react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CircleCheck, CircleX } from "lucide-react";
import { api, qs } from "@/lib/api";
import { BRANDS } from "@/lib/brands";
import { formatDateTime } from "@/lib/format";
import { BrandMark } from "@/components/BrandMark";
import { Empty, PageHeader } from "@/components/PageHeader";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";

interface Suppression {
  id: number;
  email: string;
  brand_id: number | null;
  reason: string;
  source: string;
  detail: string | null;
  contact_id: number | null;
  created_at: string;
}

interface Mailability {
  email: string;
  can_send: boolean;
  reasons: string[];
  address_statuses: string[];
}

export const SUPPRESSION_REASON: Record<string, { label: string; tone: "destructive" | "warning" | "muted" | "default"; protects?: boolean }> = {
  hard_bounce: { label: "Bounced: address doesn't exist", tone: "destructive", protects: true },
  repeated_soft_bounce: { label: "Kept bouncing", tone: "destructive", protects: true },
  complaint: { label: "Marked as spam", tone: "destructive", protects: true },
  unsubscribe: { label: "Unsubscribed", tone: "muted" },
  keap_optout: { label: "Opted out in Keap", tone: "muted" },
  manual: { label: "Blocked by staff", tone: "warning" },
  invalid: { label: "Not a real address", tone: "warning" },
};

const SOURCE: Record<string, string> = {
  keap_import: "Keap",
  ses: "Amazon SES report",
  postmark: "Postmark report",
  admin: "Admin screen",
  mcp: "Claude",
  api: "API",
  site: "Website",
  system: "CRM",
};

const sourceLabel = (s: string) => SOURCE[s] ?? s.replace(/_/g, " ");

/** A whole address uses the exact lookup; anything else (part of one, or @domain for a domain and its
 *  subdomains) uses suppressions_list's partial search. */
const isAddress = (t: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(t);
const searchParams = (t: string) => (!t ? {} : isAddress(t) ? { email: t } : { search: t });

/** Email → Blocked addresses: every address that never gets marketing email, why, and since when. */
export function SuppressionsPage() {
  const [email, setEmail] = useState("");
  const [search, setSearch] = useState("");
  const [reason, setReason] = useState("");
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<Suppression | null>(null);
  const q = useInfiniteQuery({
    queryKey: ["suppressions", search, reason],
    enabled: search !== "@",
    queryFn: ({ pageParam }) =>
      api<{ suppressions: Suppression[]; next_before_id?: number }>("/api/v1/suppressions" + qs({ ...searchParams(search), reason: reason || undefined, before_id: pageParam || undefined, limit: 100 })),
    initialPageParam: 0,
    getNextPageParam: (last) => last.next_before_id || undefined,
  });
  const rows = q.data?.pages.flatMap((p) => p.suppressions) ?? [];
  // Search as they type, a moment after they stop.
  useEffect(() => {
    const t = setTimeout(() => setSearch(email.trim()), 300);
    return () => clearTimeout(t);
  }, [email]);
  return (
    <div className="mx-auto max-w-[1200px] space-y-3">
      <PageHeader
        title="Blocked addresses"
        sub="Never sent marketing email"
        actions={
          <Button size="sm" onClick={() => setAdding(true)}>
            Block an address
          </Button>
        }
      >
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Bounces, spam complaints and unsubscribes are added automatically. Keap's opt-outs and bounces were imported only from Jan 1, 2024 on; older Keap opt-outs aren't listed
          here, but their Opt-Out tag (923) still keeps them out of every segment. Receipts and sign-in emails still go out.
        </p>
      </PageHeader>

      <CheckCard />

      <Card className="overflow-x-auto">
        <CardHeader className="flex-wrap gap-2">
          <CardTitle>Blocked</CardTitle>
          <form
            className="ml-auto flex flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setSearch(email.trim());
            }}
          >
            <Input className="h-8 w-72" type="search" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Address, part of one, or @domain.com" aria-label="Find blocked addresses" />
            <Select className="h-8 w-auto" value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Reason">
              <option value="">Every reason</option>
              {Object.entries(SUPPRESSION_REASON).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </Select>
          </form>
        </CardHeader>
        {q.isLoading && <Skeleton className="m-3 h-32" />}
        {search === "@" && <p className="px-4 py-3 text-sm text-[var(--color-muted-foreground)]">Add a domain after the @, for example @example.com.</p>}
        {q.error && <p className="px-4 py-3 text-sm text-[var(--color-danger-fg)]">{q.error.message}</p>}
        {!q.isLoading && !q.error && search !== "@" && rows.length === 0 && (
          <Empty title={search ? (isAddress(search) ? `${search} isn't blocked.` : `Nothing blocked matches “${search}”.`) : "Nothing blocked yet."}>
            {search && isAddress(search) ? "Check it above to see every reason it might not get email." : null}
          </Empty>
        )}
        {rows.length > 0 && (
          <Table>
            <THead>
              <tr>
                <TH>Address</TH>
                <TH>Brand</TH>
                <TH>Why</TH>
                <TH>From</TH>
                <TH>Since (MT)</TH>
                <TH className="w-px" />
              </tr>
            </THead>
            <tbody>
              {rows.map((r) => {
                const why = SUPPRESSION_REASON[r.reason];
                return (
                  <TR key={r.id}>
                    <TD className="max-w-[18rem]">
                      {r.contact_id ? (
                        <Link to="/contacts/$contactId" params={{ contactId: String(r.contact_id) }} className="block truncate text-[var(--color-primary)] hover:underline">
                          {r.email}
                        </Link>
                      ) : (
                        <span className="block truncate">{r.email}</span>
                      )}
                    </TD>
                    <TD>{r.brand_id ? <BrandMark id={r.brand_id} /> : <span className="text-xs whitespace-nowrap text-[var(--color-muted-foreground)]">All brands</span>}</TD>
                    <TD className="max-w-[18rem]">
                      <Badge variant={why?.tone ?? "muted"}>{why?.label ?? r.reason.replace(/_/g, " ")}</Badge>
                      {r.detail && <div className="mt-0.5 truncate text-xs text-[var(--color-muted-foreground)]" title={r.detail}>{r.detail}</div>}
                    </TD>
                    <TD className="whitespace-nowrap text-xs">
                      <Badge variant={r.source === "keap_import" ? "muted" : "default"} className="mr-1.5">
                        {r.source === "keap_import" ? "imported" : "CRM"}
                      </Badge>
                      {sourceLabel(r.source)}
                    </TD>
                    <TD className="num whitespace-nowrap text-xs">{formatDateTime(r.created_at)}</TD>
                    <TD>
                      <Button size="sm" variant="ghost" onClick={() => setRemoving(r)}>
                        Unblock
                      </Button>
                    </TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        )}
        {q.hasNextPage && (
          <div className="border-t p-2 text-center">
            <Button size="sm" variant="outline" disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
              Show more
            </Button>
          </div>
        )}
      </Card>
      {adding && <AddDialog onClose={() => setAdding(false)} />}
      {removing && <RemoveDialog row={removing} onClose={() => setRemoving(null)} />}
    </div>
  );
}

/** "Can we email this address?": every reason one address wouldn't get marketing email from a brand. */
function CheckCard() {
  const [email, setEmail] = useState("");
  const [brand, setBrand] = useState<number | "">("");
  const check = useMutation({
    mutationFn: () => api<Mailability>("/api/v1/suppressions/check" + qs({ email: email.trim(), brand_id: brand || undefined })),
  });
  const r = check.data;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Can we email this address?</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (email.includes("@")) check.mutate();
          }}
        >
          <Input className="h-8 w-72" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" aria-label="Address to check" />
          <Select className="h-8 w-auto" value={brand} onChange={(e) => setBrand(e.target.value ? Number(e.target.value) : "")} aria-label="From brand">
            <option value="">Any brand</option>
            {BRANDS.map((b) => (
              <option key={b.id} value={b.id}>
                From {b.short}
              </option>
            ))}
          </Select>
          <Button size="sm" variant="outline" type="submit" disabled={!email.includes("@") || check.isPending}>
            Check
          </Button>
        </form>
        {check.error && <p className="text-xs text-[var(--color-danger-fg)]">{check.error.message}</p>}
        {r && (
          <div className="rounded-md border p-2">
            <div className="flex items-center gap-1.5 font-medium">
              {r.can_send ? <CircleCheck className="size-4 text-[var(--color-success-fg)]" /> : <CircleX className="size-4 text-[var(--color-danger-fg)]" />}
              {r.can_send ? `Yes, ${r.email} can get marketing email.` : `No, ${r.email} won't get marketing email:`}
            </div>
            {r.reasons.length > 0 && (
              <ul className="mt-1 list-disc pl-6 text-xs">
                {r.reasons.map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            )}
            {r.address_statuses.length === 0 && <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">No contact in the CRM has this address.</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AddDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [brand, setBrand] = useState<number | "">("");
  const [reason, setReason] = useState<"manual" | "invalid">("manual");
  const [detail, setDetail] = useState("");
  const add = useMutation({
    mutationFn: () => api("/api/v1/suppressions", { json: { email: email.trim(), brand_id: brand || undefined, reason, detail: detail || undefined } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["suppressions"] });
      toast(`Blocked ${email.trim()}`);
      onClose();
    },
    onError: (e) => toast(e.message, "error"),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(520px,calc(100vw-2rem))]">
        <DialogTitle>Block an address</DialogTitle>
        <DialogDescription>It stops getting marketing email right away. Receipts and sign-in emails still go out. For a typo, a role address, or someone who asked by phone.</DialogDescription>
        <div className="space-y-3 text-sm">
          <div className="space-y-1">
            <Label htmlFor="sp-email">Address</Label>
            <Input id="sp-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="sp-brand">From</Label>
              <Select id="sp-brand" value={brand} onChange={(e) => setBrand(e.target.value ? Number(e.target.value) : "")}>
                <option value="">Every brand</option>
                {BRANDS.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} only
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="sp-reason">Why</Label>
              <Select id="sp-reason" value={reason} onChange={(e) => setReason(e.target.value as "manual" | "invalid")}>
                <option value="manual">They asked, or another reason</option>
                <option value="invalid">Not a real address (typo, fake)</option>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="sp-detail">Note (optional)</Label>
            <Input id="sp-detail" value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="Asked by phone, Oct 26" />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!email.includes("@") || add.isPending} onClick={() => add.mutate()}>
            Block
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RemoveDialog({ row, onClose }: { row: Suppression; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const why = SUPPRESSION_REASON[row.reason];
  const protects = !!why?.protects;
  const optedOut = row.reason === "unsubscribe" || row.reason === "keap_optout";
  const [sure, setSure] = useState(false);
  const remove = useMutation({
    mutationFn: () => api(`/api/v1/suppressions/${row.id}` + qs({ force: protects || undefined, confirm: optedOut || undefined }), { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["suppressions"] });
      toast(`Unblocked ${row.email}`);
      onClose();
    },
    onError: (e) => toast(e.message, "error"),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(520px,calc(100vw-2rem))]">
        <DialogTitle>Unblock {row.email}?</DialogTitle>
        <DialogDescription>
          {why?.label ?? row.reason} ({row.brand_id ? "one brand" : "every brand"}) since {formatDateTime(row.created_at)}. {sourceLabel(row.source)}.
        </DialogDescription>
        <div className="space-y-2 text-sm">
          {protects ? (
            <>
              <p className="text-[var(--color-warning-fg)]">
                Mailing an address that bounced or complained hurts every brand's delivery. Unblock it only if the person asked, or their mailbox was fixed, and check with Brandon first.
              </p>
              <label className="flex items-start gap-2">
                <Checkbox checked={sure} onChange={(e) => setSure(e.target.checked)} className="mt-0.5" /> The person asked for email again, or their mailbox works now, and Brandon agreed.
              </label>
            </>
          ) : optedOut ? (
            <>
              <p>They asked to stop getting email. Unblock it only if they asked to get it again.</p>
              <label className="flex items-start gap-2">
                <Checkbox checked={sure} onChange={(e) => setSure(e.target.checked)} className="mt-0.5" /> They asked to get this email again.
              </label>
            </>
          ) : (
            <p>It can get marketing email again (unless something else blocks it; check it above).</p>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" disabled={remove.isPending || ((protects || optedOut) && !sure)} onClick={() => remove.mutate()}>
            Unblock
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
